import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  BufferPoolDataRaw,
  getSegmentationBufferKeys,
  requestBufferPoolJson,
  setSharedContextData,
  useBufferPool,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import useAuth from '@hooks/Auth/useAuth';
import { SeriesAnalysisStatusMap } from './AnalysisBaseUtilities';
import { configStore } from 'config/configStore';
import { AnalysisType } from 'config/analysisConfig';
import { clearCornerstoneResource } from '@features/analysis/rendering/Panels/Renderers/CornerstoneManager';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';

//getUpdateAnalysisType
export function useAnalysisBaseUtilities() {
  const dispatch = useDispatch();
  const { selectedSeriesId, selectedSeries } = useBrowserPage();
  const {
    isLayoutStateUpdateData,
    updateSelectAnalysis,
    handleLayoutStateLoading,
    handleLayoutStateUpdateData,
  } = useAnalysisPage();
  const { bufferPool, setBufferPool, deletePoolItems } = useBufferPool();
  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;
  const { accessToken } = useAuth();
  const [refreshAnalysisData, setRefreshAnalysisData] =
    useState<boolean>(false);

  const resetSharedContext = () => {
    setSharedContextData('undefined', 'oriHUData');
    setSharedContextData('undefined', 'oriLabelData');
  };

  const clearSelectedSeriesResource = (seriesId: string) => {
    clearCornerstoneResource(seriesId, [
      `PREVIEW_${seriesId}`,
      `ORIGINAL_${seriesId}`,
    ]);
  };

  const recalculateAnalysisListData = () => {
    if (!selectedSeriesId || !pool || !selectedSeries) return;
    console.log('recalculate 데이터 날리고 MPR로 이동');
    deletePoolItems(selectedSeriesId, ['parameter'], 'excludeKeys');
    clearSelectedSeriesResource(selectedSeriesId);
    updateSelectAnalysis(selectedSeries, 'MPR');
    resetSharedContext();
  };

  const recalculateCTFFR = () => {
    if (!selectedSeriesId || !pool || !selectedSeries) return;
    console.log('CT-FFR 데이터 날리고 preview로 이동');
    deletePoolItems(selectedSeriesId, [], 'excludeKeys');
    clearSelectedSeriesResource(selectedSeriesId);
    updateSelectAnalysis(selectedSeries, 'preview');
    resetSharedContext();
    configStore.setData({shouldRunFFR:false})
  };


  const refreshData = () => {
    if (!selectedSeriesId || !pool || !selectedSeries) return;
    console.log('데이터 날리고 preview로 이동');
    deletePoolItems(selectedSeriesId, [], 'excludeKeys');
    clearSelectedSeriesResource(selectedSeriesId);
    updateSelectAnalysis(selectedSeries, 'preview');
    resetSharedContext();
  };

  const refreshParameterData = async () => {
    if (!selectedSeriesId || !pool?.parameter?.data) return;
    await requestBufferPoolJson(
      pool,
      setBufferPool,
      accessToken,
      selectedSeriesId,
      'parameter',
    );
  };

  const refreshAnalysisListData = async () => {
    if (!selectedSeriesId || !pool) return;

    resetSharedContext();
    clearSelectedSeriesResource(selectedSeriesId);

    setBufferPool((prev) => {
      const prevSeries = prev[selectedSeriesId] ?? {};
      const updatedSeries = { ...prevSeries };

      //VR pool
      const volumeMapperData = pool?.segmentationVolumeMapper;
      if (volumeMapperData) {
        updatedSeries.segmentationVolumeMapper = {
          ...volumeMapperData,
          loaded: false,
          loading: false,
          data: undefined,
        };
      }

      // segmentation buffer
      getSegmentationBufferKeys('segmentation').forEach((key) => {
        const keyData = pool[key] as BufferPoolDataRaw;
        if (keyData.data) return;
        updatedSeries[key] = {
          ...keyData,
          loading: false,
          loaded: false,
          data: undefined,
        };
      });

      return {
        ...prev,
        [selectedSeriesId]: updatedSeries,
      };
    });
  };

  const getSeriesAnalysisStatus = (
    seriesId: string,
    analysisBaseInfoBySeries: SeriesAnalysisStatusMap,
  ) => {
    const rawBaseInfo = analysisBaseInfoBySeries[seriesId] ?? [];
    if (!rawBaseInfo.length) {
      return { anySucceeded: false, segSucceeded: false };
    }

    const statusByName = new Map(
      rawBaseInfo.map((item) => [
        item?.name?.toLowerCase() ?? '',
        item?.status,
      ]),
    );

    const analysisStatus = configStore.get().analysisStatus;
    const nextStatus = { ...analysisStatus };
    let changed = false;
    let segSucceeded = false;
    let anySucceeded = false;

    console.log('나유', analysisStatus);

    for (const name of Object.keys(analysisStatus)) {
      const key = name.toLowerCase() as keyof typeof analysisStatus;
      const status = statusByName.get(key);
      if (!status) continue;

      const prev = analysisStatus[key];
      if (prev === status) continue;

      changed = true;
      if (status === 'Succeeded') {
        anySucceeded = true;
      }
      if (key === 'segmentation') {
        segSucceeded = status === 'Succeeded';
      }
      (nextStatus as typeof analysisStatus)[key] = status;
    }

    if (changed) {
      console.log('나유', nextStatus);
      configStore.setData({ analysisStatus: nextStatus });
    }

    return { anySucceeded, segSucceeded };
  };

  const runRefreshData = async (
    analysisType: AnalysisType,
    isLoading: boolean,
    isUpdata?: boolean,
  ) => {
    if (!selectedSeriesId || !pool || !analysisType) return;

    console.log('데이터 바꿉니다.');
    if (isLoading) {
      handleLayoutStateLoading(true);
    }

    await refreshParameterData();
    await refreshAnalysisListData();
    if (isUpdata) {
      if (isLayoutStateUpdateData) {
        handleLayoutStateLoading(false);
      }
      handleLayoutStateUpdateData(true);
    }
  };

  return {
    refreshAnalysisData,
    getSeriesAnalysisStatus,
    runRefreshData,
    recalculateAnalysisListData,
    setRefreshAnalysisData,
    recalculateCTFFR,
    refreshData,
  };
}
